'use client';

import { SvgMinus } from '@bazaar-marketplace/assets/minus';
import { SvgPlus } from '@bazaar-marketplace/assets/plus';
import { Typography } from '~/components';
import type { ReactNode } from 'react';
import { cn } from '@bazaar-marketplace/utils';

interface CountSelectorProps {
  value: number;
  minValue: number;
  maxValue?: number;
  onChange: (newValue: number) => void;
  label?: string;
  maxInfo?: string;
  disabled?: boolean;
  className?: string;
  buttonGridClassName?: string;
  countButtonClassName?: string;
}

interface CountButtonProps {
  icon: ReactNode;
  disabled?: boolean;
  onAdjust: () => void;
  className?: string;
}

function CountButton({ icon, disabled, onAdjust, className }: CountButtonProps) {
  return (
    <button
      className={cn(
        'flex items-center h-12 justify-center bg-white/10 rounded-2xl disabled:opacity-50 disabled:border-0 border border-white/20',
        className,
      )}
      disabled={disabled}
      type="button"
      onClick={onAdjust}
    >
      {icon}
    </button>
  );
}

interface AdjustButtonProps {
  disabled?: boolean;
  onAdjust: (delta: number) => void;
  className?: string;
}

function DecrementButton({ disabled, onAdjust, className }: AdjustButtonProps) {
  return (
    <CountButton
      className={className}
      disabled={disabled}
      icon={<SvgMinus className="size-4" />}
      onAdjust={() => {
        onAdjust(-1);
      }}
    />
  );
}

function IncrementButton({ disabled, onAdjust, className }: AdjustButtonProps) {
  return (
    <CountButton
      className={className}
      disabled={disabled}
      icon={<SvgPlus className="size-4" />}
      onAdjust={() => {
        onAdjust(1);
      }}
    />
  );
}

export function CountSelector({
  value,
  minValue,
  maxValue,
  onChange,
  label,
  maxInfo,
  disabled = false,
  className,
  countButtonClassName,
  buttonGridClassName,
}: CountSelectorProps) {
  const adjustCount = (delta: number) => {
    const newValue = Math.max(minValue, Math.min(maxValue ?? Infinity, value + delta));
    onChange(newValue);
  };

  const isDecrementDisabled = disabled || value <= minValue;
  const isIncrementDisabled = disabled || (maxValue != null && value >= maxValue);

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {label || maxInfo ? (
        <div className="flex items-center justify-between gap-3">
          <Typography className="text-white font-quicksand font-bold text-lg">{label}</Typography>
          {maxInfo ? (
            <Typography className="text-white/40 font-quicksand font-bold text-sm">{maxInfo}</Typography>
          ) : null}
        </div>
      ) : null}
      <div className={cn('grid grid-cols-[80px_auto_80px] gap-2', buttonGridClassName)}>
        <DecrementButton className={countButtonClassName} disabled={isDecrementDisabled} onAdjust={adjustCount} />
        <div className="flex-1 flex items-center justify-center bg-black border border-white/20 rounded-2xl">
          <div className="bg-transparent text-white/80 font-quicksand font-bold text-[20px] text-center w-full outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none">
            {value}
          </div>
        </div>
        <IncrementButton className={countButtonClassName} disabled={isIncrementDisabled} onAdjust={adjustCount} />
      </div>
    </div>
  );
}
